import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import permissionsService, {
  Role,
  PermissionsByModule,
} from '../services/permissions.service';
import { PermissionGate } from '../components/PermissionGate';

interface RolePermissionState {
  [roleId: string]: Set<string>;
}

const PermissionsManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissionsByModule, setPermissionsByModule] = useState<PermissionsByModule>({});
  const [rolePermissions, setRolePermissions] = useState<RolePermissionState>({});
  const [originalRolePermissions, setOriginalRolePermissions] = useState<RolePermissionState>({});
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Color mapping for modules
  const moduleColors: { [key: string]: string } = {
    companies: '#1976d2',
    persons: '#2e7d32',
    reports: '#ed6c02',
    'wind-up': '#9c27b0',
    'data-quality': '#0288d1',
    'audit-logs': '#d32f2f',
    documents: '#f57c00',
    enrichment: '#7b1fa2',
    users: '#c62828',
    roles: '#5e35b1',
    system: '#424242',
  };

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load roles and permissions in parallel
      const [rolesData, permissionsData] = await Promise.all([
        permissionsService.getRoles(),
        permissionsService.getPermissionsByModule(),
      ]);

      setRoles(rolesData);
      setPermissionsByModule(permissionsData);

      // Load permissions for each role
      const rolePermsState: RolePermissionState = {};
      await Promise.all(
        rolesData.map(async (role) => {
          const rolePermData = await permissionsService.getRolePermissions(role.id);
          rolePermsState[role.id] = new Set(rolePermData.permissions.map((p) => p.id));
        })
      );

      setRolePermissions(rolePermsState);
      setOriginalRolePermissions(JSON.parse(JSON.stringify(rolePermsState)));
      setHasChanges(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load permissions data');
      console.error('Error loading permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (roleId: string, permissionId: string) => {
    setRolePermissions((prev) => {
      const newState = { ...prev };
      if (!newState[roleId]) {
        newState[roleId] = new Set();
      }

      const rolePerms = new Set(newState[roleId]);
      if (rolePerms.has(permissionId)) {
        rolePerms.delete(permissionId);
      } else {
        rolePerms.add(permissionId);
      }
      newState[roleId] = rolePerms;

      // Check if there are changes
      checkForChanges(newState);

      return newState;
    });
  };

  const checkForChanges = (newState: RolePermissionState) => {
    let changed = false;
    for (const roleId in newState) {
      const original = originalRolePermissions[roleId] || new Set();
      const current = newState[roleId];

      if (original.size !== current.size) {
        changed = true;
        break;
      }

      for (const permId of current) {
        if (!original.has(permId)) {
          changed = true;
          break;
        }
      }

      if (changed) break;
    }
    setHasChanges(changed);
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    setError(null);
    try {
      // Save changes for each role
      await Promise.all(
        roles.map(async (role) => {
          const currentPerms = Array.from(rolePermissions[role.id] || []);
          await permissionsService.assignPermissionsToRole(role.id, currentPerms);
        })
      );

      setOriginalRolePermissions(JSON.parse(JSON.stringify(rolePermissions)));
      setHasChanges(false);
      setSuccessMessage('Permissions updated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save permissions');
      console.error('Error saving permissions:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleResetChanges = () => {
    setRolePermissions(JSON.parse(JSON.stringify(originalRolePermissions)));
    setHasChanges(false);
  };

  const getPermissionCount = (roleId: string): number => {
    return rolePermissions[roleId]?.size || 0;
  };

  const getTotalPermissions = (): number => {
    return Object.values(permissionsByModule).reduce((acc, perms) => acc + perms.length, 0);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <PermissionGate permission="roles:manage" fallback={
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don't have permission to manage roles and permissions.
        </Alert>
      </Container>
    }>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Permissions Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage role permissions across all modules
              </Typography>
            </Box>
          </Stack>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveChanges}
              disabled={!hasChanges || saving}
            >
              Save Changes
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleResetChanges}
              disabled={!hasChanges || saving}
            >
              Reset
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading || saving}
            >
              Refresh
            </Button>
          </Stack>

          {/* Status Messages */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {hasChanges && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You have unsaved changes. Click "Save Changes" to apply them.
            </Alert>
          )}

          {/* Statistics */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h3" color="primary.main">
                  {roles.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Roles
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h3" color="secondary.main">
                  {getTotalPermissions()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Permissions
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h3" color="success.main">
                  {Object.keys(permissionsByModule).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Modules
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h3" color="warning.main">
                  {hasChanges ? '!' : '✓'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {hasChanges ? 'Unsaved' : 'Saved'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Permission Matrix by Module */}
        <Box>
          {Object.entries(permissionsByModule).map(([module, permissions]) => (
            <Accordion key={module} defaultExpanded={module === 'companies'}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Chip
                    label={module.toUpperCase()}
                    sx={{
                      bgcolor: moduleColors[module] || '#757575',
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {permissions.length} permissions
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>
                          Permission
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>
                          Description
                        </TableCell>
                        {roles.map((role) => (
                          <TableCell key={role.id} align="center" sx={{ fontWeight: 'bold' }}>
                            <Tooltip title={role.description || role.name}>
                              <Box>
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                  {role.name.replace(/_/g, ' ')}
                                </Typography>
                                <Chip
                                  label={getPermissionCount(role.id)}
                                  size="small"
                                  sx={{ mt: 0.5, fontSize: '0.7rem' }}
                                />
                              </Box>
                            </Tooltip>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {permissions.map((permission) => (
                        <TableRow key={permission.id} hover>
                          <TableCell>
                            <Chip
                              label={permission.name}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{permission.description}</Typography>
                          </TableCell>
                          {roles.map((role) => (
                            <TableCell key={role.id} align="center">
                              <Checkbox
                                checked={rolePermissions[role.id]?.has(permission.id) || false}
                                onChange={() => handlePermissionToggle(role.id, permission.id)}
                                disabled={saving}
                                color="primary"
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSuccessMessage(null)}
            severity="success"
            sx={{ width: '100%' }}
            icon={<CheckCircleIcon />}
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </Container>
    </PermissionGate>
  );
};

export default PermissionsManagement;
